// @flow
import {edgeStyle} from './edgeStyle';
import type {Node, Edge, Element} from '../types';

type NodeChildren = Array<any>;
type NodeAttributes = {[string]: any};

// Edge labels are rendered as SVGs
// Extract the string label to pass to the edge
const extractLabel = (children: NodeChildren): string => {
  if (!children || children.length === 0) {
    return '';
  }
  const edge = children[0];
  const label = edge.children?.length ? edge.children[0] : null;
  if (!edge || !label) {
    return '';
  }
  if (typeof label === 'string') {
    return label;
  }
  if (label.name === 'InlineCode') {
    return label.attributes.content;
  }
  return '';
};

const buildStateDiagramNode = (
  attributes: NodeAttributes,
  children: NodeChildren,
): Node => {
  const {id, state, x, y} = attributes;
  const node: Node = {
    id,
    type: 'state',
    position: {x, y}, // position the nodes after the initial render so we know their height / widths
    data: {text: children},
  };

  if (state) {
    node.data.state = state;
  }

  return node;
};

const buildStateDiagramEdge = (
  attributes: NodeAttributes,
  children: NodeChildren,
): Edge => {
  const {from, to, fromPosition, toPosition, style} = attributes;
  const label = extractLabel(children);

  const edge: Edge = {
    id: `edge-${from}-${to}-${fromPosition}-${toPosition}`,
    source: from,
    target: to,
    arrowHeadType: 'arrowclosed',
    type: style,
    ...edgeStyle,
  };
  if (label) {
    edge.label = label;
  }
  if (fromPosition !== 'auto') {
    edge.sourceHandle = fromPosition;
  }
  if (toPosition !== 'auto') {
    edge.targetHandle = toPosition;
  }
  return edge;
};

export const buildStateDiagram = (
  attributes: NodeAttributes,
  children: NodeChildren | null,
): Array<Element> => {
  if (!children) {
    return [];
  }

  const elements = children.map((c) => {
    if (c.name !== 'node' && c.name !== 'edge') {
      // We should never get here due to Markdoc tag validation
      return null;
    }
    return c.name === 'node'
      ? buildStateDiagramNode(c.attributes, c.children)
      : buildStateDiagramEdge(c.attributes, c.children);
  });
  return elements.filter(Boolean);
};
